import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaFileInvoiceDollar,
  FaCar,
  FaDownload,
  FaWallet,
} from "react-icons/fa";
import api from "../api/Api";

export default function PaymentHistory() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTxnId, setExpandedTxnId] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await api.get("/bookingRoutes/payment-history", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setPayments(response.data?.payments || []);
      } catch (error) {
        console.error("Error fetching payments", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const toggleDetails = (index) => {
    setExpandedTxnId(expandedTxnId === index ? null : index);
  };

  const getStatusConfig = (status) => {
    const safeStatus = status ? status.toLowerCase() : "pending";
    switch (safeStatus) {
      case "success":
        return {
          icon: <FaCheckCircle size={12} />,
          label: "Success",
          cls: "status-success",
        };
      case "failed":
        return {
          icon: <FaTimesCircle size={12} />,
          label: "Failed",
          cls: "status-failed",
        };
      case "pending":
        return {
          icon: <FaClock size={12} />,
          label: "Pending",
          cls: "status-pending",
        };
      default:
        return { icon: null, label: status, cls: "status-default" };
    }
  };

  return (
    <div className="ph-root">
      {/* ── HEADER ── */}
      <div className="ph-header">
        <div className="ph-header-blob" />
        <div className="ph-header-inner">
          <div className="ph-header-row">
            <div className="ph-header-icon-wrap">
              <FaWallet size={20} color="#fff" />
            </div>
            <div>
              <h1 className="ph-title">Payment History</h1>
              <p className="ph-subtitle">Your transactions &amp; invoices</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="ph-body">
        {/* LOADING */}
        {loading && (
          <div className="ph-center-state">
            <div className="ph-spinner" />
            <p className="ph-state-text">Fetching transactions…</p>
          </div>
        )}

        {/* EMPTY */}
        {!loading && payments.length === 0 && (
          <div className="ph-empty-card">
            <div className="ph-empty-icon">
              <FaFileInvoiceDollar size={32} />
            </div>
            <h3 className="ph-empty-title">No Payments Yet</h3>
            <p className="ph-empty-desc">
              You haven't made any booking payments yet.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="ph-cta-btn"
            >
              Book a Ride
            </button>
          </div>
        )}

        {/* LIST */}
        {!loading &&
          payments.map((txn, index) => {
            const sc = getStatusConfig(txn.status);
            const isOpen = expandedTxnId === index;
            const dateObj = new Date(txn.date);
            const dateStr = dateObj.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            });
            const timeStr = dateObj.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            });

            return (
              <div
                key={index}
                className="ph-card"
                style={{ animationDelay: `${index * 0.07}s` }}
              >
                {/* TOP ROW: amount + status */}
                <div className="ph-card-top">
                  <div className="ph-amount-block">
                    <span className="ph-currency">₹</span>
                    <span className="ph-amount">
                      {txn.amount?.toLocaleString()}
                    </span>
                  </div>
                  <span className={`ph-status-badge ${sc.cls}`}>
                    {sc.icon}
                    <span>{sc.label}</span>
                  </span>
                </div>

                {/* MID ROW: car + date */}
                <div className="ph-card-mid">
                  <div className="ph-car-row">
                    <FaCar size={12} className="ph-car-icon" />
                    <span className="ph-car-name">{txn.carName}</span>
                  </div>
                  <span className="ph-datetime">
                    {dateStr} · {timeStr}
                  </span>
                </div>

                {/* REF + METHOD ROW */}
                <div className="ph-card-ref">
                  <div className="ph-ref-item">
                    <span className="ph-ref-label">Ref</span>
                    <span className="ph-ref-value">{txn.bookingId}</span>
                  </div>
                  <div className="ph-method-pill">
                    <span className="ph-method-mode">{txn.mode}</span>
                    <span className="ph-method-via">via {txn.method}</span>
                  </div>
                </div>

                {/* DIVIDER */}
                <div className="ph-divider" />

                {/* ACTION BUTTONS */}
                {txn.status === "success" && (
                  <div className="ph-actions">
                    <button
                      onClick={() => toggleDetails(index)}
                      className={`ph-btn-details ${isOpen ? "ph-btn-details--active" : ""}`}
                    >
                      {isOpen ? "Hide Details" : "View Details"}
                    </button>
                    <button
                      className="ph-btn-invoice"
                      onClick={(e) => {
                        e.stopPropagation();
                        const total = Number(txn.amount) || 0;
                        const base = Math.round(total * 0.85);
                        const taxAndFees = Math.round(total * 0.05);
                        const driverfee = Math.round(total * 0.1);
                        navigate("/invoice", {
                          state: {
                            trip: {
                              bookingId: txn.confirmId || txn.bookingId,
                              date: txn.date,
                              customerName:
                                txn.customerName || "Valued Customer",
                              customerMobile: txn.customerMobile || "N/A",
                              pickup: txn.pickupLocation || "Pickup Location",
                              drop: txn.dropoffLocation || "Drop-off Location",
                              carName: txn.carName || "Assigned Cab",
                              totalFare: total,
                              baseFare: base,
                              taxes: taxAndFees,
                              driver: driverfee,
                              discount: 0,
                            },
                          },
                        });
                      }}
                    >
                      <FaDownload size={11} />
                      <span>Invoice</span>
                    </button>
                  </div>
                )}

                {txn.status === "failed" && (
                  <div className="ph-failed-note">
                    <FaTimesCircle size={12} />
                    <span>This payment failed or was cancelled.</span>
                  </div>
                )}

                {/* EXPANDED DETAILS */}
                {isOpen && (
                  <div className="ph-details-panel">
                    <p className="ph-details-heading">Transaction Details</p>
                    <div className="ph-detail-row">
                      <span className="ph-detail-label">Transaction ID</span>
                      <span className="ph-detail-value ph-mono">
                        {txn.id || "N/A"}
                      </span>
                    </div>
                    <div className="ph-detail-row">
                      <span className="ph-detail-label">Payment Type</span>
                      <span className="ph-detail-value">{txn.mode}</span>
                    </div>
                    <div className="ph-detail-row">
                      <span className="ph-detail-label">Gateway Method</span>
                      <span className="ph-detail-value">{txn.method}</span>
                    </div>
                    <div className="ph-detail-row">
                      <span className="ph-detail-label">Booking Ref</span>
                      <span className="ph-detail-value ph-ref-highlight">
                        {txn.confirmId}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* ── STYLES ── */}
      <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                .ph-root {
                    font-family: 'Plus Jakarta Sans', sans-serif;
                    background: #F2F4F8;
                    min-height: 100vh;
                    -webkit-font-smoothing: antialiased;
                }

                /* ── HEADER ── */
                .ph-header {
                    position: relative;
                    background: linear-gradient(145deg, #1A1A2E 0%, #16213E 40%, #0F3460 100%);
                    padding: 30px 20px;
                    overflow: hidden;
                    border-radius: 0 0 32px 32px;
                    box-shadow: 0 8px 32px rgba(15,52,96,0.25);
                }
                .ph-header-blob {
                    position: absolute;
                    width: 220px; height: 220px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(99,179,237,0.18) 0%, transparent 70%);
                    top: -60px; right: -40px;
                    pointer-events: none;
                }
                .ph-header-inner { position: relative; z-index: 1; }
                .ph-header-row {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .ph-header-icon-wrap {
                    width: 44px; height: 44px;
                    border-radius: 14px;
                    background: rgba(255,255,255,0.12);
                    display: flex; align-items: center; justify-content: center;
                    backdrop-filter: blur(8px);
                    border: 1px solid rgba(255,255,255,0.15);
                }
                .ph-title {
                    font-size: 20px;
                    font-weight: 800;
                    color: #fff;
                    letter-spacing: -0.3px;
                    line-height: 1.2;
                }
                .ph-subtitle {
                    font-size: 12px;
                    color: rgba(255,255,255,0.55);
                    font-weight: 500;
                    margin-top: 2px;
                }

                /* ── BODY ── */
                .ph-body {
                    padding: 20px 16px 40px;
                    max-width: 520px;
                    margin: 0 auto;
                }

                /* ── STATES ── */
                .ph-center-state {
                    display: flex; flex-direction: column;
                    align-items: center; justify-content: center;
                    padding: 72px 0;
                    gap: 12px;
                }
                .ph-spinner {
                    width: 36px; height: 36px;
                    border: 3px solid #e2e8f0;
                    border-top-color: #0F3460;
                    border-radius: 50%;
                    animation: spin 0.7s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                .ph-state-text { font-size: 13px; color: #94a3b8; font-weight: 600; }

                .ph-empty-card {
                    background: #fff;
                    border-radius: 24px;
                    padding: 48px 24px;
                    text-align: center;
                    margin-top: 8px;
                    box-shadow: 0 2px 16px rgba(0,0,0,0.06);
                }
                .ph-empty-icon {
                    width: 72px; height: 72px;
                    background: #F2F4F8;
                    border-radius: 50%;
                    display: inline-flex; align-items: center; justify-content: center;
                    color: #cbd5e1;
                    margin-bottom: 16px;
                }
                .ph-empty-title { font-size: 17px; font-weight: 800; color: #1e293b; margin-bottom: 6px; }
                .ph-empty-desc { font-size: 13px; color: #94a3b8; font-weight: 500; margin-bottom: 20px; }
                .ph-cta-btn {
                    background: #0F3460;
                    color: #fff;
                    border: none;
                    border-radius: 50px;
                    padding: 12px 28px;
                    font-size: 14px;
                    font-weight: 700;
                    font-family: inherit;
                    cursor: pointer;
                    transition: background 0.2s, transform 0.15s;
                }
                .ph-cta-btn:active { transform: scale(0.97); }

                /* ── CARD ── */
                .ph-card {
                    background: #fff;
                    border-radius: 20px;
                    padding: 18px 18px 14px;
                    margin-bottom: 12px;
                    box-shadow: 0 1px 8px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.04);
                    animation: slideUp 0.4s ease-out both;
                    border: 1px solid rgba(0,0,0,0.04);
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* TOP ROW */
                .ph-card-top {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 10px;
                }
                .ph-amount-block {
                    display: flex;
                    align-items: baseline;
                    gap: 2px;
                }
                .ph-currency {
                    font-size: 15px;
                    font-weight: 700;
                    color: #475569;
                    line-height: 1;
                }
                .ph-amount {
                    font-size: 26px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: -1px;
                    line-height: 1;
                }

                /* STATUS BADGE */
                .ph-status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 11.5px;
                    font-weight: 700;
                    padding: 5px 10px;
                    border-radius: 50px;
                    letter-spacing: 0.2px;
                }
                .status-success { background: #dcfce7; color: #15803d; }
                .status-failed  { background: #fee2e2; color: #b91c1c; }
                .status-pending { background: #fef9c3; color: #92400e; }
                .status-default { background: #f1f5f9; color: #475569; }

                /* MID ROW */
                .ph-card-mid {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }
                .ph-car-row {
                    display: flex; align-items: center; gap: 6px;
                }
                .ph-car-icon { color: #94a3b8; }
                .ph-car-name { font-size: 13px; font-weight: 600; color: #334155; }
                .ph-datetime { font-size: 11.5px; color: #94a3b8; font-weight: 500; }

                /* REF + METHOD */
                .ph-card-ref {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 14px;
                }
                .ph-ref-item { display: flex; align-items: center; gap: 6px; }
                .ph-ref-label {
                    font-size: 10.5px;
                    font-weight: 700;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    padding: 2px 7px;
                    border-radius: 4px;
                }
                .ph-ref-value {
                    font-size: 12px;
                    font-weight: 700;
                    color: #1e293b;
                    font-variant-numeric: tabular-nums;
                }
                .ph-method-pill {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 50px;
                    padding: 4px 10px;
                }
                .ph-method-mode {
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #334155;
                }
                .ph-method-via {
                    font-size: 10.5px;
                    color: #94a3b8;
                    font-weight: 500;
                }

                /* DIVIDER */
                .ph-divider {
                    height: 1px;
                    background: repeating-linear-gradient(
                        90deg,
                        #e2e8f0 0px, #e2e8f0 6px,
                        transparent 6px, transparent 12px
                    );
                    margin-bottom: 12px;
                }

                /* ACTIONS */
                .ph-actions {
                    display: flex;
                    gap: 10px;
                }
                .ph-btn-details {
                    flex: 1;
                    background: #EFF6FF;
                    color: #1d4ed8;
                    border: 1.5px solid #bfdbfe;
                    border-radius: 12px;
                    padding: 10px 0;
                    font-size: 13px;
                    font-weight: 700;
                    font-family: inherit;
                    cursor: pointer;
                    transition: all 0.18s;
                }
                .ph-btn-details--active {
                    background: #f1f5f9;
                    color: #475569;
                    border-color: #e2e8f0;
                }
                .ph-btn-details:active { transform: scale(0.97); }

                .ph-btn-invoice {
                    flex: 1;
                    display: flex; align-items: center; justify-content: center; gap: 6px;
                    background: #0F3460;
                    color: #fff;
                    border: none;
                    border-radius: 12px;
                    padding: 10px 0;
                    font-size: 13px;
                    font-weight: 700;
                    font-family: inherit;
                    cursor: pointer;
                    transition: all 0.18s;
                }
                .ph-btn-invoice:active { transform: scale(0.97); background: #0a2545; }

                /* FAILED NOTE */
                .ph-failed-note {
                    display: flex; align-items: center; gap: 6px;
                    background: #fff5f5;
                    border: 1px solid #fecaca;
                    border-radius: 10px;
                    padding: 10px 14px;
                    color: #dc2626;
                    font-size: 12.5px;
                    font-weight: 600;
                }

                /* EXPANDED DETAILS */
                .ph-details-panel {
                    margin-top: 14px;
                    background: #f8fafc;
                    border-radius: 14px;
                    padding: 14px 16px;
                    border: 1px solid #e2e8f0;
                    animation: fadeDown 0.2s ease-out;
                }
                @keyframes fadeDown {
                    from { opacity: 0; transform: translateY(-6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .ph-details-heading {
                    font-size: 10px;
                    font-weight: 800;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-bottom: 10px;
                }
                .ph-detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 7px 0;
                    border-bottom: 1px solid #f1f5f9;
                }
                .ph-detail-row:last-child { border-bottom: none; padding-bottom: 0; }
                .ph-detail-label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 500;
                }
                .ph-detail-value {
                    font-size: 12px;
                    font-weight: 700;
                    color: #1e293b;
                    text-align: right;
                    max-width: 60%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .ph-mono { font-variant-numeric: tabular-nums; font-size: 11.5px; }
                .ph-ref-highlight { color: #1d4ed8; }
            `}</style>
    </div>
  );
}